import { TypeManagerDecorator } from "../../main/type.map.service";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from "@angular/core";
import { S25Util } from "../../util/s25-util";
import {
    IngestionPoint,
    IngestionPointCategory,
    IngestionSubmission,
    DataIngestionService,
} from "./data.ingestion.service";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";
import { UserprefService } from "../../services/userpref.service";
import { OrganizationService } from "../../services/organization.service";
import { AlertService } from "../../services/alert.service";
import { Workflow } from "../workflow/service/Workflow";

@TypeManagerDecorator("s25-ng-ingestion-dashboard")
@Component({
    selector: "s25-ng-ingestion-dashboard",
    template: `
        @if (init) {
            <!-- Dashboard View -->
            <div class="c-margin-left--single c-margin-top--single" [hidden]="!showDashboard">
                <!-- Always show refresh button -->
                <div class="section-header">
                    <h3 class="c-margin-bottom--single">Form Submissions</h3>
                    <s25-ng-button [type]="'outline'" [onClick]="refresh" [buttonClass]="'refresh-button'">
                        <i class="fa fa-refresh"></i> Refresh
                    </s25-ng-button>
                </div>

                @if (availableForms?.length) {
                    <div class="section">
                        <!-- Campus-Wide Forms Section -->
                        @if (categoryWideForms?.length) {
                            <div class="organization-group c-margin-bottom--double">
                                <div class="category-wide-header c-margin-bottom--single">
                                    <h4 class="category-wide-title">Campus-Wide Forms</h4>
                                </div>
                                <div class="forms-grid">
                                    @for (form of categoryWideForms; track form.ingestionPointId) {
                                        <div class="form-card">
                                            <h4>{{ form.ingestionPointName }}</h4>
                                            @if (form.description) {
                                                <p class="form-description">{{ form.description }}</p>
                                            }
                                            <s25-ng-button
                                                [type]="'primary'"
                                                [onClick]="startForm"
                                                [onClickArgs]="[form.ingestionPointId]"
                                            >
                                                Start Form
                                            </s25-ng-button>
                                        </div>
                                    }
                                </div>
                            </div>
                        }

                        <!-- Organization-Specific Forms Section -->
                        @for (subCatId of sortedSubCatIds; track subCatId) {
                            <div class="organization-group c-margin-bottom--double">
                                @if (selectedCategory === "ENGAGEMENT" && subCatId) {
                                    <div class="organization-header c-margin-bottom--single">
                                        <s25-item-organization
                                            [itemId]="subCatId"
                                            [itemName]="organizationNames.get(subCatId)"
                                        >
                                        </s25-item-organization>
                                    </div>
                                }
                                <div class="forms-grid">
                                    @if (selectedCategory === "ENGAGEMENT" && subCatId) {
                                        <!-- Create Event Button - Special first entry -->
                                        <div class="form-card create-event-card">
                                            <h4>Create an Event</h4>
                                            <p class="form-description">Create a new event for this organization</p>
                                            <s25-ng-button
                                                [type]="'primary'"
                                                [onClick]="startEventCreation"
                                                [onClickArgs]="[subCatId]"
                                            >
                                                Create Event
                                            </s25-ng-button>
                                        </div>
                                    }

                                    @for (form of formsBySubCat.get(subCatId); track form.ingestionPointId) {
                                        <div class="form-card">
                                            <h4>{{ form.ingestionPointName }}</h4>
                                            @if (form.description) {
                                                <p class="form-description">{{ form.description }}</p>
                                            }
                                            <s25-ng-button
                                                [type]="'primary'"
                                                [onClick]="startForm"
                                                [onClickArgs]="[form.ingestionPointId]"
                                            >
                                                Start Form
                                            </s25-ng-button>
                                        </div>
                                    }
                                </div>
                            </div>
                        }
                    </div>
                }

                @if (inProgressSubmissions?.length) {
                    <div class="section">
                        <h3 class="c-margin-bottom--single">In Progress Forms</h3>
                        <div class="submissions-list">
                            @for (submission of inProgressSubmissions; track submission.submissionId) {
                                <div class="submission-card in-progress">
                                    <div class="submission-header">
                                        <h4>
                                            {{ submission.ingestionPoint?.ingestionPointName || "Form Submission" }}
                                        </h4>
                                        <span
                                            class="submission-status"
                                            [class.requested-changes-status]="
                                                submission.workflowStatus === 'REQUESTED_CHANGES'
                                            "
                                        >
                                            {{
                                                submission.workflowStatus === "REQUESTED_CHANGES"
                                                    ? "Changes Requested"
                                                    : "In Progress"
                                            }}
                                        </span>
                                    </div>
                                    <p class="submission-meta">
                                        Last modified: {{ formatDate(submission.lastModDate) }}
                                    </p>
                                    @if (submission.workflowStatus === "REQUESTED_CHANGES") {
                                        <s25-ng-requested-changes
                                            [itemTypeId]="WORKFLOW_INGESTION_SUBMISSION_OBJECT_TYPE"
                                            [itemId]="submission.submissionId"
                                        ></s25-ng-requested-changes>
                                    }
                                    <div class="submission-actions">
                                        <s25-ng-button
                                            [type]="'primary'"
                                            [onClick]="continueForm"
                                            [onClickArgs]="[submission.submissionId]"
                                            [buttonClass]="'c-margin-right--single'"
                                        >
                                            Continue
                                        </s25-ng-button>
                                        <s25-ng-button
                                            [type]="'danger--outline'"
                                            [onClick]="deleteSubmission"
                                            [onClickArgs]="[submission.submissionId]"
                                        >
                                            Delete
                                        </s25-ng-button>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                }

                @if (submittedSubmissions?.length) {
                    <div class="section">
                        <h3 class="c-margin-bottom--single">Submitted Forms</h3>
                        <div class="submissions-list">
                            @for (submission of submittedSubmissions; track submission.submissionId) {
                                <div class="submission-card submitted">
                                    <div class="submission-header">
                                        <h4>{{ submission.ingestionPoint?.ingestionPointName || "Submitted Form" }}</h4>
                                        <span class="submission-status">Submitted</span>
                                    </div>
                                    <p class="submission-meta">Submitted: {{ formatDate(submission.lastModDate) }}</p>
                                    <div class="submission-actions">
                                        <s25-ng-button
                                            [type]="'outline'"
                                            [onClick]="viewSubmission"
                                            [onClickArgs]="[submission.submissionId]"
                                        >
                                            View
                                        </s25-ng-button>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                }

                @if (!availableForms?.length && !inProgressSubmissions?.length && !submittedSubmissions?.length) {
                    <div class="empty-state">
                        <h3>No forms available</h3>
                        <p>
                            There are currently no forms available for you to fill out. Use the refresh button above to
                            check for newly created forms.
                        </p>
                    </div>
                }
            </div>

            <!-- Form Outlet -->
            @if (!showDashboard && currentIngestionPointId) {
                <s25-ng-ingestion-form
                    [submissionId]="currentSubmissionId"
                    [ingestionPointId]="currentIngestionPointId"
                    [readOnly]="isReadOnlyView"
                    [showCloseButton]="true"
                    (onClose)="onFormClose()"
                    (onSubmit)="onFormSubmit()"
                ></s25-ng-ingestion-form>
            }

            <!-- Event Creation Form Outlet -->
            @if (!showDashboard && showEventCreationForm) {
                <s25-ng-event-creation-form-wrapper
                    [organizationId]="currentOrganizationId"
                    [mode]="'create'"
                    [onFormClose]="onEventFormClose"
                    [onFormSave]="onEventFormSave"
                ></s25-ng-event-creation-form-wrapper>
            }
        }
    `,
    styles: `
        .section {
            margin-bottom: 2rem;
        }

        .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1rem;
        }

        .section-header h3 {
            margin: 0;
        }

        .refresh-button {
            font-size: 0.9rem;
        }

        .refresh-button i {
            margin-right: 0.5rem;
        }

        .forms-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 1rem;
            margin-bottom: 2rem;
        }

        .form-card {
            border: 1px solid #ddd;
            border-radius: 8px;
            padding: 1.5rem;
            background-color: #f9f9f9;
        }

        .form-card h4 {
            margin: 0 0 0.5rem 0;
            color: #2573a7;
        }

        .form-description {
            margin: 0 0 1rem 0;
            color: #666;
        }

        .submissions-list {
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }

        .submission-card {
            border: 1px solid #ddd;
            border-radius: 8px;
            padding: 1.5rem;
            background-color: #fff;
        }

        .submission-card.in-progress {
            border-left: 4px solid #ffc107;
        }

        .submission-card.submitted {
            border-left: 4px solid #28a745;
        }

        .submission-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 0.5rem;
        }

        .submission-header h4 {
            margin: 0;
            color: #2573a7;
        }

        .submission-status {
            font-weight: bold;
            padding: 0.25rem 0.5rem;
            border-radius: 4px;
            font-size: 0.9rem;
        }

        .submission-card.in-progress .submission-status {
            background-color: #fff3cd;
            color: #856404;
        }

        .submission-card.submitted .submission-status {
            background-color: #d4edda;
            color: #155724;
        }

        .submission-meta {
            margin: 0 0 1rem 0;
            color: #666;
            font-size: 0.9rem;
        }

        .submission-actions {
            display: flex;
            gap: 0.5rem;
        }

        .requested-changes-status {
            background-color: #f8d7da !important;
            color: #721c24 !important;
        }

        .empty-state {
            text-align: center;
            padding: 3rem;
            color: #666;
        }

        .empty-state h3 {
            margin: 0 0 1rem 0;
        }

        .empty-state p {
            margin: 0;
        }

        .category-wide-header {
            border-bottom: 2px solid #2573a7;
            padding-bottom: 0.5rem;
        }

        .category-wide-title {
            margin: 0;
            color: #2573a7;
            font-size: 1.1rem;
            font-weight: 600;
        }

        .create-event-card {
            border: 2px solid #2573a7;
            background-color: #f0f8ff;
        }

        .create-event-card h4 {
            color: #2573a7;
            font-weight: 600;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class IngestionDashboardComponent implements OnInit {
    availableForms: IngestionPoint[];
    inProgressSubmissions: IngestionSubmission[];
    submittedSubmissions: IngestionSubmission[];
    init = false;
    dateTimeFormat = "MM/dd/yyyy h:mm a";

    // Category management
    @Input() selectedCategory: IngestionPointCategory = IngestionPointCategory.ENGAGEMENT;

    // Organization grouping
    formsBySubCat = new Map<number, IngestionPoint[]>();
    categoryWideForms: IngestionPoint[] = [];
    organizationNames = new Map<number, string>();
    sortedSubCatIds: number[] = [];

    // Form outlet management
    showDashboard: boolean = true;
    currentSubmissionId: number | null = null;
    currentIngestionPointId: number | null = null;
    isReadOnlyView: boolean = false;

    // Event creation form management
    showEventCreationForm: boolean = false;
    currentOrganizationId: number | null = null;
    isEventCreationInProgress: boolean = false;

    constructor(public cd: ChangeDetectorRef) {}

    ngOnInit() {
        this.refresh();
    }

    refresh = async () => {
        try {
            this.init = false;
            this.cd.detectChanges();

            const [forms, submissions, dateTimeFormat] = await Promise.all([
                DataIngestionService.getIngestionPoints(this.selectedCategory),
                DataIngestionService.getMySubmissions(),
                UserprefService.getS25DateTimeformat(),
            ]);

            // Process forms data
            await this.processAvailableForms(forms);

            this.dateTimeFormat = dateTimeFormat;

            // Separate submissions by status
            this.inProgressSubmissions = (submissions || []).filter((s) => !s.submitted);
            this.submittedSubmissions = (submissions || []).filter((s) => s.submitted);

            // Sort by last modified date (newest first)
            this.inProgressSubmissions.sort(
                (a, b) => new Date(b.lastModDate || 0).getTime() - new Date(a.lastModDate || 0).getTime(),
            );
            this.submittedSubmissions.sort(
                (a, b) => new Date(b.lastModDate || 0).getTime() - new Date(a.lastModDate || 0).getTime(),
            );

            this.init = true;
            this.cd.detectChanges();
        } catch (error) {
            console.error("Failed to load dashboard:", error);
            S25Util.showError("Failed to load dashboard");
        }
    };

    private async processAvailableForms(forms: IngestionPoint[]) {
        // Filter to only show enabled forms
        this.availableForms = (forms || []).filter((form) => form.enabled);

        // Group forms by subCat (organization ID for ENGAGEMENT category)
        await this.groupForms();
    }

    private async groupForms() {
        this.formsBySubCat.clear();
        this.categoryWideForms = [];
        const subCatIds = new Set<number>();

        for (let form of this.availableForms) {
            const subCatId = form.subCat ? parseInt(form.subCat) || 0 : 0; // Default to 0 for forms without subCat

            if (subCatId === 0) {
                // Category-wide forms - track separately
                this.categoryWideForms.push(form);
            } else {
                // Organization-specific forms
                if (!this.formsBySubCat.has(subCatId)) {
                    this.formsBySubCat.set(subCatId, []);
                }
                this.formsBySubCat.get(subCatId)!.push(form);
                subCatIds.add(subCatId);
            }
        }

        // Fetch organization names for ENGAGEMENT category (excluding 0)
        if (this.selectedCategory === IngestionPointCategory.ENGAGEMENT && subCatIds.size > 0) {
            try {
                const organizations = await OrganizationService.getOrganizationsById(Array.from(subCatIds));
                this.organizationNames.clear();
                for (let org of organizations || []) {
                    const orgId = S25Util.parseInt(org.organization_id);
                    this.organizationNames.set(orgId, org.organization_name);
                }

                // Sort subCat IDs by organization name
                this.sortedSubCatIds = Array.from(this.formsBySubCat.keys()).sort((a, b) => {
                    const nameA = this.organizationNames.get(a) || "";
                    const nameB = this.organizationNames.get(b) || "";
                    return nameA.localeCompare(nameB);
                });
            } catch (error) {
                console.error("Failed to fetch organization names:", error);
                // Fallback to numeric sort if organization fetch fails
                this.sortedSubCatIds = Array.from(this.formsBySubCat.keys()).sort((a, b) => a - b);
            }
        } else if (this.selectedCategory === IngestionPointCategory.EVENT_SURVEY) {
            // EVENT_SURVEY forms are standalone - no grouping needed
            // Just put all forms in category-wide
            this.formsBySubCat.clear();
            this.categoryWideForms = [...this.availableForms];
            this.sortedSubCatIds = [];
        } else {
            // For other categories, just sort numerically
            this.sortedSubCatIds = Array.from(this.formsBySubCat.keys()).sort((a, b) => a - b);
        }
    }

    startForm = async (ingestionPointId: number) => {
        try {
            this.currentIngestionPointId = ingestionPointId;
            this.isReadOnlyView = false;
            this.showDashboard = false;
            this.cd.detectChanges();
        } catch (error) {
            console.error("Failed to start form:", error);
            S25Util.showError("Failed to start form");
        }
    };

    continueForm = (submissionId: number) => {
        // Find the ingestion point ID from the submission
        try {
            const submission = this.inProgressSubmissions.find((s) => s.submissionId === submissionId);
            this.currentSubmissionId = submissionId;
            this.currentIngestionPointId = submission!.ingestionPointId!;
            this.isReadOnlyView = false;
            this.showDashboard = false;
            this.cd.detectChanges();
        } catch (error) {
            console.error("Failed to continue form:", error);
            S25Util.showError("Failed to continue form");
        }
    };

    viewSubmission = (submissionId: number) => {
        // Find the ingestion point ID from the submission
        try {
            const submission = this.submittedSubmissions.find((s) => s.submissionId === submissionId);
            this.currentSubmissionId = submissionId;
            this.currentIngestionPointId = submission!.ingestionPointId!;
            this.isReadOnlyView = true;
            this.showDashboard = false;
            this.cd.detectChanges();
        } catch (error) {
            console.error("Failed to view submission:", error);
            S25Util.showError("Failed to view submission");
        }
    };

    onFormClose = () => {
        return this.onFormSubmit();
    };

    onFormSubmit = async () => {
        this.showDashboard = true;
        this.currentSubmissionId = null;
        this.currentIngestionPointId = null;
        this.isReadOnlyView = false;
        await this.refresh(); // Refresh to update the dashboard
    };

    deleteSubmission = async (submissionId: number) => {
        if (!(await AlertService.confirm("Are you sure you want to delete this form? This action cannot be undone."))) {
            return;
        }
        try {
            await DataIngestionService.deleteSubmission(submissionId);
            await this.refresh();
        } catch (error) {
            console.error("Failed to delete form:", error);
            S25Util.showError(error);
        }
    };

    formatDate = (dateString: string): string => {
        return S25Datefilter.transform(dateString, this.dateTimeFormat) || "";
    };

    startEventCreation = (organizationId: number) => {
        // Prevent rapid clicking/smashing
        if (this.isEventCreationInProgress) {
            return;
        }

        this.isEventCreationInProgress = true;
        this.currentOrganizationId = organizationId;
        this.showEventCreationForm = true;
        this.showDashboard = false;
        this.cd.detectChanges();
    };

    onEventFormClose = () => {
        this.showEventCreationForm = false;
        this.currentOrganizationId = null;
        this.isEventCreationInProgress = false; // Reset the flag when form is closed
        this.showDashboard = true;
        this.cd.detectChanges();
    };

    onEventFormSave = (eventId: number) => {
        console.log("Event created successfully with ID:", eventId);
        this.onEventFormClose();
    };

    readonly WORKFLOW_INGESTION_SUBMISSION_OBJECT_TYPE = Workflow.WORKFLOW_INGESTION_SUBMISSION_OBJECT_TYPE;
}
